/** @format */

import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { catchError, take, tap, timeout } from 'rxjs/operators';
import {
	AnalyticsService,
	DeviceService,
	KillswitchService,
	PromoteIQService,
	UtilityService
} from '@shared/data-access/services';

import { CriteoService } from '@shared/data-access/product-recommendations';
/* eslint-disable @typescript-eslint/naming-convention */
import { ProductListApiService } from './product-list-api.service';
import { ProductSelectionStateService } from './product-selection-state.service';
import {
	setCriteoProducts,
	setPinnedContent,
	setPiqProducts
} from '@plp/data-access/state/product-list.action';
import { PinnedContent, ProductList } from '@plp/data-access/interfaces';
import { HomrDataService } from '@shared/homr/data-access';
import { BreakPointNew } from '@shared/data-access/enums';
import {
	DEFAULT_PAGE_SIZE,
	DSG_DESKTOP_BROWSE_PLA_ID,
	DSG_DESKTOP_SEARCH_PLA_ID,
	DSG_MOBILE_BROWSE_PLA_ID,
	DSG_MOBILE_SEARCH_PLA_ID,
	PAGE_SIZE_OPTIONS,
	PERFORMANCE_PAGE_SIZE,
	PERFORMANCE_PAGE_SIZE_OPTIONS
} from '@plp/data-access/constants';
import { throwError } from 'rxjs';
import { Chain, CriteoViewProductsMetadata, ProductTypes } from '@shared/data-access/interfaces';

@Injectable({
	providedIn: 'root'
})
export class ProductListUIService {
	triggerSponsoredProductsIntendedEvent = false;
	usePerformancePageSize = false;

	constructor(
		private store: Store,
		private homrDataService: HomrDataService,
		private promoteIQService: PromoteIQService,
		private productSelectionStateService: ProductSelectionStateService,
		private readonly productListApiService: ProductListApiService,
		private deviceService: DeviceService,
		private chain: Chain,
		private analyticsService: AnalyticsService,
		private killSwitchService: KillswitchService,
		private utilityService: UtilityService,
		private readonly criteoService: CriteoService,
		@Inject(PLATFORM_ID) private platformId: string
	) {
		this.killSwitchService
			.getKillSwitchesEnvironment$()
			.pipe(take(1))
			.subscribe((data) => {
				this.triggerSponsoredProductsIntendedEvent = this.utilityService.checkKillSwitch(
					'triggerSponsoredProductsIntendedEvent',
					data,
					this.chain.chainIdentifierAbbr
				);
				this.usePerformancePageSize = this.utilityService.checkKillSwitch(
					'usePerformancePageSize',
					data,
					this.chain.chainIdentifierAbbr
				);
			});
	}

	setPinnedContentHomrDataToState(productListData: ProductList, seoUrlToken: string): void {
		if (!isPlatformBrowser(this.platformId) || productListData.searchVO.pageNumber > 0) {
			this.store.dispatch(setPinnedContent({ pinnedContent: {} }));
			return;
		}

		this.homrDataService
			.getData$('pinned-content', 2, seoUrlToken)
			.pipe(
				timeout(10000),
				take(1),
				tap((pinnedContent: PinnedContent) => {
					const csid = pinnedContent?.['contentScheduleMeta']?.['pinned-content']?.csid;
					const pinnedContentData = pinnedContent?.['content']?.['pinned-content'];
					const pinnedContentSpots = {};
					if (Array.isArray(pinnedContentData)) {
						pinnedContentData.forEach((spot) => {
							const index = spot.pinnedPosition - 1;
							pinnedContentSpots[index] = spot;
							pinnedContentSpots[index].csid = csid;
						});
					}

					this.store.dispatch(setPinnedContent({ pinnedContent: pinnedContentSpots }));
				}),
				catchError((err) => {
					this.store.dispatch(setPinnedContent({ pinnedContent: {} }));
					return throwError(err);
				})
			)
			.subscribe();
	}

	setPromoteIQProductsToState(productListData, catgroupId?: number) {
		const chainIdentifierAbbr = this.chain.chainIdentifierAbbr;
		if (
			(chainIdentifierAbbr === 'dsg' || chainIdentifierAbbr === 'gg') &&
			isPlatformBrowser(this.platformId) &&
			productListData.searchVO.pageNumber === 0
		) {
			let piqCount = Math.ceil((productListData.productVOs.length - 4) / 2);
			if (piqCount < 0) {
				piqCount = 0;
			}

			const store = chainIdentifierAbbr === 'gg' ? chainIdentifierAbbr : '';

			const top10ParentPartNumbers = productListData.productVOs
				.slice(0, 10)
				.map((product) => {
					return store
						? `${store}_${product.parentPartnumber}`
						: product.parentPartnumber;
				});
			const params = this.promoteIQService.createParams(
				{ match_products: top10ParentPartNumbers },
				piqCount <= 11 ? piqCount : 11,
				this.getInGridSlotID(),
				catgroupId,
				store
			);
			this.promoteIQService
				.getPromoteIQData$(params)
				.pipe(timeout(10000), take(2))
				.subscribe({
					next: (piqProducts) => {
						this.store.dispatch(setPiqProducts({ piqProducts }));
						// eslint-disable-next-line no-unused-expressions
						this.triggerSponsoredProductsIntendedEvent &&
							this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
								event: 'sponsoredProductsIntended',
								value: 'true'
							});
					},
					error: (error) => {
						this.store.dispatch(setPiqProducts({ piqProducts: [] }));
						console.error(error);
					}
				});
		} else {
			this.store.dispatch(setPiqProducts({ piqProducts: [] }));
			// eslint-disable-next-line no-unused-expressions
			this.triggerSponsoredProductsIntendedEvent &&
				this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
					event: 'sponsoredProductsIntended',
					value: 'false'
				});
		}
	}

	setFilteredCriteoProductsToState(
		metadata: CriteoViewProductsMetadata,
		filters: Record<string, string[]>
	): void {
		metadata.filters =
			Object.keys(filters).length > 0 ? this.criteoService.parseFilters(filters) : null;
		if (metadata.productType === ProductTypes.VIEW_CATEGORY) {
			metadata.category = this.criteoService.transformToCriteoBreadcrumbFormat(
				metadata.category
			);
		}
		this.criteoService.viewProductsPage(metadata).subscribe({
			next: (criteoProducts) => {
				this.store.dispatch(setCriteoProducts({ criteoProducts }));
				this.criteoService.fireBeacon(
					criteoProducts?.placements[0]?.carousel[0]?.OnLoadBeacon
				);
			},
			error: () => {
				this.store.dispatch(setCriteoProducts({ criteoProducts: null }));
			}
		});
	}

	getInGridSlotID() {
		const isFamilyPage = this.productSelectionStateService.getPageTypeData() === 'plp';
		const breakpoint = this.deviceService.getDeviceBreakPointNew();
		const isMobile =
			breakpoint === BreakPointNew.SMALL ||
			breakpoint === BreakPointNew.EXTRASMALL ||
			BreakPointNew.EXTRAEXTRASMALL;
		if (isFamilyPage) {
			if (isMobile) {
				// If it's a family page and mobile use DSG-Mobile-Browse-PLA Slot ID
				return DSG_MOBILE_BROWSE_PLA_ID;
			}
			// If it's family page and not mobile use DSG-Desktop-Browse-PLA Slot ID
			return DSG_DESKTOP_BROWSE_PLA_ID;
		}

		if (isMobile) {
			// If it's a search page and mobile use DSG-Mobile-Search-PLA Slot ID
			return DSG_MOBILE_SEARCH_PLA_ID;
		}
		// If it's a search page and not mobile use DSG-Desktop-Search-PLA Slot ID
		return DSG_DESKTOP_SEARCH_PLA_ID;
	}

	/**
	 * @description Function is used to format pricing data to be displayed for the pricing checkboxes.
	 */
	addSymbolForPriceDisplay(value: string): string {
		let valArray = [];
		let displayText = '';
		if (value) {
			valArray = value.split('-');
			if (valArray[0] && !isNaN(valArray[0])) {
				valArray[0] = parseFloat(valArray[0].trim()).toFixed(0);
			}
			if (valArray[1] && !isNaN(valArray[1])) {
				valArray[1] = parseFloat(valArray[1].trim()).toFixed(0);
			}
		}
		displayText = `$${valArray[0]} - $${valArray[1]}`;
		if (valArray[0] === '0' || valArray[0] === '*') {
			displayText = `Under $${valArray[1]}`;
		}
		if (valArray[1] === '*') {
			displayText = `$${valArray[0]} & Above`;
		}
		return displayText;
	}

	/**
	 * @description Returns the pageSize depending whether there is an assigned value, otherwise returns the default value.
	 */
	getPageSize(pageSize: string): number {
		return pageSize ? parseInt(pageSize, 10) : this.getDefaultProductListPageSize();
	}

	/**
	 * @description Returns the pageNumber depending whether there is an assigned value, otherwise returns the default value.
	 */
	getPageNumber(pageNumber: string): number {
		return pageNumber ? parseInt(pageNumber, 10) : 0;
	}

	/**
	 * @description Returns the default pageSize.
	 */
	getDefaultProductListPageSize(): number {
		return this.usePerformancePageSize ? PERFORMANCE_PAGE_SIZE : DEFAULT_PAGE_SIZE;
	}

	getPageSizeOptions(): number[] {
		if (!this.usePerformancePageSize) return PAGE_SIZE_OPTIONS;
		return this.getPerformancePageSizeOptions();
	}

	getPerformancePageSizeOptions(): number[] {
		const isFamilyPage = this.productListApiService.getPageType() === 'plp';
		return this.usePerformancePageSize && isFamilyPage
			? PERFORMANCE_PAGE_SIZE_OPTIONS
			: PAGE_SIZE_OPTIONS;
	}
}
